import React from 'react';
import Grid from '@mui/material/Grid';
import {
  FormContextType,
  ObjectFieldTemplateProps,
  RJSFSchema,
  StrictRJSFSchema,
  canExpand,
  descriptionId,
  getTemplate,
  getUiOptions,
  titleId
} from '@rjsf/utils';
import { Box, darken, useTheme } from '@mui/material';

/** The `ObjectFieldTemplate` is the template to use to render all the inner properties of an object along with the
 * title and description if available. If the object is expandable, then an `AddButton` is also rendered after all
 * the properties.
 *
 * @param props - The `ObjectFieldTemplateProps` for this component
 */

const getNumber = (id: string) => {
  const trackId = id.split('_');
  const lastNumber = trackId[trackId.length - 1];
  return lastNumber ? Number(lastNumber) + 1 : '';
};

export default function ObjectFieldTemplate<T = any, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = any>(
  props: ObjectFieldTemplateProps<T, S, F>
) {
  const { description, title, properties, required, disabled, readonly, uiSchema, idSchema, schema, formData, onAddClick, registry, formContext } =
    props;

  const number = getNumber(idSchema.$id);
  const uiOptions = getUiOptions<T, S, F>(uiSchema);
  const TitleFieldTemplate = getTemplate<'TitleFieldTemplate', T, S, F>('TitleFieldTemplate', registry, uiOptions);
  const DescriptionFieldTemplate = getTemplate<'DescriptionFieldTemplate', T, S, F>('DescriptionFieldTemplate', registry, uiOptions);
  // Button templates are not overridden in the uiSchema
  const {
    ButtonTemplates: { AddButton }
  } = registry.templates;
  const theme = useTheme();

  const pseudoValueStyling = {
    '&, &.Mui-disabled': {
      background: 'none !important',
      paddingLeft: 0,
      paddingRight: 0,
      opacity: 1,
      paddingTop: theme.spacing(2.5),
      WebkitTextFillColor: theme.palette.text.primary,
      color: theme.palette.text.primary,
      caretColor: 'transparent',
      '& .MuiSvgIcon-root': { display: 'none' }
    }
  };

  const pseudoLabelStyling = {
    '&, &.Mui-disabled': {
      color: theme.palette.mode === 'light' ? 'primary.dark' : 'primary.light',
      opacity: 0.7,
      transform: 'none',
      fontSize: '0.86rem',
      '& .MuiFormLabel-asterisk': { display: 'none' }
    }
  };

  return (
    <>
      {(uiOptions.title || title) && (
        <Box sx={{ color: 'primary.main', fontSize: '1rem' }}>
          <TitleFieldTemplate
            id={titleId<T>(idSchema)}
            title={`${title}${number ? ` ${number}` : ''}`}
            required={required}
            schema={schema}
            uiSchema={uiSchema}
            registry={registry}
          />
        </Box>
      )}
      {(uiOptions.description || description) && (
        <DescriptionFieldTemplate
          id={descriptionId<T>(idSchema)}
          description={uiOptions.description || description!}
          schema={schema}
          uiSchema={uiSchema}
          registry={registry}
        />
      )}
      <Grid container rowSpacing={formContext?.rowSpacing || 2} columnSpacing={formContext?.columnSpacing || 2}>
        {properties.map((prop) => {
          const propSchema = prop.content.props?.schema || {};
          const ui_cols = propSchema.ui_cols || { xs: 12 };
          // sometimes the BE returns it as `readOnly`
          const schemaReadOnly = readonly || propSchema.readonly || propSchema.readOnly;
          const isAnyOrOneOf = !!(propSchema.anyOf || propSchema.oneOf || propSchema.items?.anyOf || propSchema.items?.oneOf);

          return (
            <Grid
              item
              key={prop.name}
              {...ui_cols}
              sx={{
                '& .MuiPaper-root': schemaReadOnly
                  ? {}
                  : { backgroundColor: (theme) => darken(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.2 : 0.02) },
                '& .MuiInputBase-root, & .MuiInputBase-root:hover': schemaReadOnly ? pseudoValueStyling : {},
                '& .MuiInputBase-input.Mui-disabled': { color: theme.palette.text.primary, WebkitTextFillColor: theme.palette.text.primary },
                '& .MuiFormLabel-root': schemaReadOnly ? pseudoLabelStyling : {},
                '& .MuiTypography-subtitle2': schemaReadOnly ? { display: 'none' } : {},
                '& .panel.panel-body > .form-group > .MuiFormControl-root.MuiTextField-root': isAnyOrOneOf ? { display: 'none' } : {}
              }}
            >
              {prop.content}
            </Grid>
          );
        })}
        {canExpand<T, S, F>(schema, uiSchema, formData) && (
          <Grid container justifyContent="flex-end">
            <Grid item>
              <AddButton
                className="object-property-expand"
                onClick={onAddClick(schema)}
                disabled={disabled || readonly}
                uiSchema={uiSchema}
                registry={registry}
              />
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  );
}
